import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {SearchBar, SearchBarSize} from '@Components/search-bar';
import {SearchBarTypes} from '@Components/search-bar/search-bar.types';
import {noop} from '@Utils/general.util';
import styles from './expandable-search-bar.module.scss';

interface ExpandableSearchBarProps {
  isExpanded?: boolean; // differentiates between the mobile view (isExpanded = true) and otherwise
  placeholder?: string;
  onInputChange?: (searchTerm: string) => void;
  onFocus?: VoidFunction;
  onSearchSubmit?: (searchTerm: string) => void;
  classes?: string;
  size?: SearchBarSize;
  initialValue?: string;
}

export function ExpandableSearchBar({
  isExpanded = false,
  placeholder = window.i18next.t('pmwjs_search'),
  onInputChange = noop,
  onFocus = noop,
  onSearchSubmit = (): void => {},
  classes = '',
  size = SearchBarSize.MEDIUM,
  initialValue = '',
}: ExpandableSearchBarProps): ReactElement {
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [inputSearchTerm, setInputSearchTerm] = useState<string>('');
  const searchBarInputRef = useRef<HTMLInputElement>(null);

  const toggleIsSelected = (): void => {
    if (!isExpanded) {
      setIsSelected(!isSelected);
    }
  };

  const onBlurInput = (event: React.FocusEvent): void => {
    if (shouldBlur(event)) {
      handleBlur();
    }
  };

  const shouldBlur = (event: React.FocusEvent): boolean => {
    const firstChild = event.relatedTarget?.firstElementChild;
    const isPreventBlur = firstChild?.classList.contains('js-prevent-blur');
    if (isPreventBlur) {
      if (searchBarInputRef && searchBarInputRef.current) {
        searchBarInputRef.current.focus();
      }

      return false;
    }

    return true;
  };

  const handleInputChange = (newInput: string): void => {
    onInputChange(newInput);
    setInputSearchTerm(newInput);
  };

  const onSubmit = (): void => {
    onSearchSubmit(inputSearchTerm);
  };

  const onClear = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (searchBarInputRef && searchBarInputRef.current) {
      searchBarInputRef.current.focus();
    }
    handleInputChange('');
  };

  const handleBlur = (): void => {
    if (inputSearchTerm === '') {
      if (!isExpanded) {
        toggleIsSelected();
      }
    }
  };

  function getClassesForSearchbar(): string {
    if (isExpanded || (isSelected && size === SearchBarSize.MEDIUM)) {
      if (isExpanded && size === SearchBarSize.SMALL) {
        return `${styles.focusState} body-xxs ${styles.fadeInElement} ${styles.searchContainerSmall}`;
      }
      return `${styles.focusState} body-xs ${styles.searchContainerMedium}`;
    }

    if (!isSelected) {
      return `${styles.focusState} body-xxs ${styles.noWidth}`;
    }

    return `${styles.focusState} body-xxs ${styles.fadeInElement} ${styles.searchContainerSmall}`;
  }

  function getClassesForIcon(): string {
    if (size === SearchBarSize.MEDIUM) {
      return styles.searchIconContainerMedium;
    }

    if (size === SearchBarSize.SMALL) {
      return styles.searchIconContainerSmall;
    }

    return '';
  }

  function getClassesForSearchParent(): string {
    let styleClasses = '';

    if (size === SearchBarSize.MEDIUM) {
      styleClasses += `body-xs ${styles.searchParentMedium}`;
    }

    if (size === SearchBarSize.SMALL) {
      styleClasses = `body-xxs ${styles.searchParentSmall}`;
    }

    styleClasses += ` ${classes}`;
    return styleClasses;
  }

  function isClickable(): boolean {
    return !isSelected && !isExpanded;
  }

  useEffect(() => {
    const shouldClearInput = isSelected || inputSearchTerm === '';
    if (searchBarInputRef && searchBarInputRef.current && shouldClearInput) {
      searchBarInputRef.current.value = '';
      if (isSelected) {
        searchBarInputRef.current.focus();
      }
    }
  }, [isSelected, inputSearchTerm]);

  return (
    <div className={getClassesForSearchParent()}>
      <SearchBar
        onFocus={onFocus}
        onSubmit={onSubmit}
        placeholder={placeholder}
        onBlur={(e: React.FocusEvent): void => {
          onBlurInput(e);
        }}
        onInput={handleInputChange}
        className={`${getClassesForSearchbar()}`}
        ref={searchBarInputRef}
        isExpandedView={isExpanded}
        searchBarType={SearchBarTypes.EXPANDABLE_SEARCH}
        initialValue={initialValue}
        size={size}
      />
      {inputSearchTerm === '' ? (
        <div tabIndex={0}>
          <span className={`${getClassesForIcon()} js-prevent-blur`}>
            <Icon
              className="content-body"
              size={size === SearchBarSize.MEDIUM ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_16}
              icon="icon-search"
              type={isClickable() ? IconType.GHOST : IconType.NONE}
              shape={isClickable() ? IconShape.SQUARE : IconShape.NONE}
              onClick={isClickable() ? toggleIsSelected : (): void => {}}
            />
          </span>
        </div>
      ) : (
        <div>
          <span className={`${getClassesForIcon()} ${styles.clickableIcon}`}>
            <Icon
              className="content-body"
              size={size === SearchBarSize.MEDIUM ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_16}
              icon="icon-close"
              type={IconType.GHOST}
              onClick={(e: React.MouseEvent): void => {
                onClear(e);
              }}
            />
          </span>
        </div>
      )}
    </div>
  );
}

export default ExpandableSearchBar;
